import {
  AppBar,
  Button,
  Grid,
  InputAdornment,
  InputBase,
  inputBaseClasses,
  Paper,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import usePagination from '@mui/material/usePagination/usePagination';
import { DocumentDownload, Magnifier } from 'asset';
import { HeadTable, Layout, TableCaption } from 'components';
import { drawerWidth } from 'config';
import FileSaver from 'file-saver';
import { getSortItem, hasPermission, NotifyService } from 'helpers';
import { useDateTime } from 'hooks';
import useDebounce from 'hooks/useDebounce';
import {
  PARENT_PACKAGE_LIST_PARAMS_REQUEST,
  PARENT_PACKAGE_TYPE,
} from 'models/parent-package';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { exportParentPackageCSVApi } from 'services/parent-packages';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setParentPackageListParams } from 'store/reducers/parent-packages';
import { getParentPackageListAction } from 'store/reducers/parent-packages/actionType';
import { theme } from 'theme';
import { headCells } from './data';

const ParentOverviews = () => {
  const dispatch = useAppDispatch();
  const { formatDateTime } = useDateTime();
  const { parent_package_overview } = useAppSelector(
    (state) => state.parentPackageReducer
  );
  const { params } = parent_package_overview;

  const [keyword, setKeyword] = useState<string>('');

  const onSetListParams = (
    params: PARENT_PACKAGE_LIST_PARAMS_REQUEST | null
  ) => {
    dispatch(setParentPackageListParams(params));
  };

  useEffect(() => {
    if (params) {
      dispatch(getParentPackageListAction(params));
    }
  }, [params, dispatch]);

  useEffect(() => {
    setKeyword(params?.keyword ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const limit = parseInt(event.target.value, 10);
    onSetListParams({ ...params, page: 1, limit });
  };

  const { items } = usePagination({
    count: parent_package_overview.metadata.total_pages,
    page: parent_package_overview.metadata.page,
    onChange(_event, page) {
      onSetListParams({ ...params, page: page });
    },
  });

  const onSetKeywordParams = useDebounce((keyword: string) => {
    const _params = { ...params, page: 1 };
    if (keyword) _params.keyword = keyword;
    else delete _params.keyword;
    onSetListParams(_params);
  }, 250);

  const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
    onSetKeywordParams(event.target.value);
  };

  const sortItem = useMemo(() => {
    return getSortItem(parent_package_overview.params?.sort);
  }, [parent_package_overview.params]);

  const handleRequestSort = (
    _: React.MouseEvent<unknown>,
    property: keyof PARENT_PACKAGE_TYPE
  ) => {
    const isAsc = sortItem?.orderBy === property && sortItem?.order === 'asc';
    onSetListParams({
      ...params,
      sort: `${property}-${isAsc ? 'desc' : 'asc'}`,
    });
  };

  const onExportCSV = async () => {
    await exportParentPackageCSVApi({
      sort: params?.sort,
      ...params,
    })
      .then((res) => {
        FileSaver.saveAs(
          res.data,
          `${moment().format('YYYY-MM-DD')} Parent Packages Overview.csv`
        );
      })
      .catch((e) => NotifyService.error(e));
  };

  return (
    <Layout>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          backgroundColor: 'white.light',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Grid>
            <Typography
              sx={{
                fontSize: '20px',
                lineHeight: 1,
                color: 'grey1.light',
              }}
            >
              Concierge / Overviews
            </Typography>
          </Grid>
          <Grid display="flex" gap="8px" flexGrow={1} justifyContent="center">
            <InputBase
              placeholder="Search"
              sx={{
                maxWidth: '369px',
                height: '41px',
                flexGrow: 1,
                background: '#F2F4F6',
                border: '1px solid #D9D9D9',
                p: '0px 7px 0px 11px',
                borderRadius: '5px',
                [`& .${inputBaseClasses.input}`]: {
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: '100%',
                  p: 0,
                  height: 'fit-content',
                },
              }}
              onChange={onChangeSearch}
              value={keyword}
              endAdornment={
                <InputAdornment position="end">
                  <Magnifier />
                </InputAdornment>
              }
            />
          </Grid>
          <Stack spacing={1} direction="row">
            {hasPermission(['superadmin']) && (
              <Button
                sx={{
                  width: '131px',
                  height: 30,
                  fontWeight: 600,
                  fontSize: '13px',
                  border: `1px solid ${theme.palette.secondary.main}`,
                  p: '0px',
                }}
                variant="outlined"
                color="secondary"
                endIcon={<DocumentDownload />}
                onClick={onExportCSV}
              >
                Export to CSV
              </Button>
            )}
          </Stack>
        </Toolbar>
      </AppBar>
      <Stack spacing="8px">
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #D9D9D9',
            maxHeight: 'calc(100vh - 150px)',
          }}
        >
          <Table
            stickyHeader
            sx={{ minWidth: 650 }}
            aria-labelledby="tableTitle"
          >
            <HeadTable<PARENT_PACKAGE_TYPE>
              order={sortItem?.order}
              orderBy={sortItem?.orderBy}
              rowCount={parent_package_overview.records.length}
              headCells={headCells}
              onCheckbox={false}
              onAction={false}
              onRequestSort={handleRequestSort}
            />
            <TableBody
              sx={{
                height: 'auto !important',
              }}
            >
              {parent_package_overview.records.map((row, index) => {
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={row.id ?? index}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row?.parent_first_name + ' ' + row?.parent_last_name}
                    </TableCell>

                    <TableCell
                      component="th"
                      scope="row"
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row?.package_name}
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      ${row?.balance}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {formatDateTime(row?.start_date)}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {formatDateTime(row?.expiry_date)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TableCaption
          rowsPerPage={params?.limit ?? 0}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          metadata={parent_package_overview.metadata}
          items={items}
        />
      </Stack>
    </Layout>
  );
};

export default ParentOverviews;
